import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock } from "lucide-react";

// ---- Pixel artwork: stylised atom (three intersecting orbital bands) ----
function AtomArt() {
  const cells = useMemo(() => {
    const size = 60;
    const out = [];
    const cx = size / 2 - 0.5, cy = size / 2 - 0.5;
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const dx = x - cx, dy = y - cy;
        const dist = Math.sqrt(dx*dx + dy*dy);
        const o1 = Math.abs(dy) < 2 && dist < 28 && dist > 8;
        const angle2 = (dy * 0.5 + dx * 0.866);
        const o2 = Math.abs(angle2) < 1.6 && dist < 28 && dist > 8;
        const angle3 = (dy * 0.5 - dx * 0.866);
        const o3 = Math.abs(angle3) < 1.6 && dist < 28 && dist > 8;
        const nucleus = dist < 6;
        const ring = dist > 26 && dist < 28 && (Math.abs(dy) < 4 || Math.abs(angle2) < 4 || Math.abs(angle3) < 4);
        const on = o1 || o2 || o3 || nucleus || ring;
        if (on) {
          const intensity = nucleus ? 1 : Math.max(0.5, 1 - dist/40);
          out.push({ x, y, intensity });
        }
      }
    }
    return out;
  }, []);

  return (
    <div className="relative w-[440px] h-[440px] max-w-full max-h-full">
      <div className="grid gap-[3px]" style={{ gridTemplateColumns: "repeat(60, 6px)" }}>
        {Array.from({length: 60*60}).map((_, i) => {
          const x = i % 60, y = Math.floor(i / 60);
          const cell = cells.find(c => c.x === x && c.y === y);
          if (!cell) return <span key={i} className="w-[6px] h-[6px]" />;
          return <span key={i} className="w-[6px] h-[6px] rounded-[1.5px]" style={{ background: `rgba(245, 154, 45, ${cell.intensity})`, boxShadow: cell.intensity > 0.9 ? "0 0 6px rgba(245,154,45,0.5)" : "none" }} />;
        })}
      </div>
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("ava.holloway@atom-aml.io");
  const [password, setPassword] = useState("any-password");
  const [tab, setTab] = useState("login");
  const [users, setUsers] = useState([]);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [institutions, setInstitutions] = useState([]);
  const [chosenInst, setChosenInst] = useState(null);
  const { login, switchInstitution } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/auth/users").then(r => setUsers(r.data.users || []));
    api.get("/institutions").then(r => {
      setInstitutions(r.data.institutions || []);
      setChosenInst(r.data.institutions?.[0] || null);
    });
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/auth/login", { email, password });
      login(r.data.user);
      switchInstitution(chosenInst);
      setWelcomeOpen(true);
    } catch (err) {
      toast.error("Unknown user");
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* LEFT — editorial heading + artwork (WeTransfer style) */}
      <div className="hidden lg:flex flex-1 flex-col justify-between px-16 py-12 relative" style={{ background: "linear-gradient(180deg, #F4F1E8 0%, #EFEBDF 100%)" }}>
        {/* Brand mark top-left */}
        <div className="flex items-center gap-2.5 z-10">
          <div className="w-7 h-7 rounded-lg bg-amber flex items-center justify-center shadow-[0_2px_6px_rgba(245,154,45,0.4)]">
            <div className="grid grid-cols-3 gap-[1.5px] w-3 h-3">
              {[1,1,1, 1,0,1, 1,1,1].map((v,i) => <span key={i} className={`w-[3px] h-[3px] ${v ? 'bg-white' : 'bg-transparent'}`} />)}
            </div>
          </div>
          <span className="display text-[16px] font-semibold tracking-tightest text-ink">Atom <span className="text-ink-muted font-normal">AML</span></span>
        </div>

        {/* Hero copy */}
        <div className="max-w-[560px] z-10">
          <h1 className="display font-semibold tracking-[-0.04em] text-ink leading-[1.02]" style={{ fontSize: "clamp(48px, 6vw, 84px)" }}>
            The simplest way to{" "}
            <span className="italic font-serif" style={{ fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 500 }}>spot financial crime</span>
          </h1>
          <p className="text-[15px] text-ink-muted mt-6 max-w-[420px] leading-relaxed">
            Audit-grade explainability, glass-box AI, and a workspace your MLRO will actually want to use. Sign in to continue.
          </p>
        </div>

        {/* Artwork pinned bottom-right */}
        <div className="absolute bottom-8 right-8 lg:bottom-12 lg:right-12 z-0 opacity-95">
          <AtomArt />
        </div>

        {/* Floating tags */}
        <div className="flex items-center gap-2 z-10">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/70 border border-line rounded-full backdrop-blur-sm text-[10px] text-ink-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-low" /> Atom Electron v1.1
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/70 border border-line rounded-full backdrop-blur-sm text-[10px] text-ink-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-low" /> Atom Proton v2.4
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/70 border border-line rounded-full backdrop-blur-sm text-[10px] text-ink-muted">
            <span className="w-1.5 h-1.5 rounded-full bg-risk-low" /> Atom Nucleus v3.5
          </span>
        </div>
      </div>

      {/* RIGHT — login pane */}
      <div className="flex-1 lg:flex-none lg:w-[520px] flex flex-col px-8 lg:px-12 py-10 relative">
        {/* Wordmark centered */}
        <div className="flex justify-center mb-12 mt-4">
          <div className="display text-[28px] font-bold tracking-[-0.03em] text-ink" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
            Atom<span className="text-amber">AML</span>
          </div>
        </div>

        <div className="w-full max-w-[360px] mx-auto flex-1 flex flex-col">
          {/* Tabs */}
          <div className="flex border-b border-line mb-7">
            <button data-testid="tab-login" onClick={() => setTab("login")} className={`flex-1 pb-3 text-[14px] font-medium tracking-tight transition-colors relative ${tab === "login" ? "text-ink" : "text-ink-subtle hover:text-ink"}`}>
              Log in
              {tab === "login" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-ink" />}
            </button>
            <button data-testid="tab-create" onClick={() => setTab("create")} className={`flex-1 pb-3 text-[14px] font-medium tracking-tight transition-colors relative ${tab === "create" ? "text-ink" : "text-ink-subtle hover:text-ink"}`}>
              Create account
              {tab === "create" && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-ink" />}
            </button>
          </div>

          {/* SSO buttons */}
          <div className="space-y-2.5">
            <button type="button" data-testid="sso-google" className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-line rounded-full text-[13px] font-medium hover:bg-bg-warm hover:border-line-strong transition-all">
              <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </button>
            <button type="button" data-testid="sso-microsoft" className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-line rounded-full text-[13px] font-medium hover:bg-bg-warm hover:border-line-strong transition-all">
              <svg width="14" height="14" viewBox="0 0 24 24"><path fill="#F25022" d="M1 1h10v10H1z"/><path fill="#7FBA00" d="M13 1h10v10H13z"/><path fill="#00A4EF" d="M1 13h10v10H1z"/><path fill="#FFB900" d="M13 13h10v10H13z"/></svg>
              Continue with Microsoft
            </button>
            <button type="button" data-testid="sso-okta" className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white border border-line rounded-full text-[13px] font-medium hover:bg-bg-warm hover:border-line-strong transition-all">
              <svg width="14" height="14" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="none" stroke="#007DC1" strokeWidth="4"/></svg>
              Continue with SSO
            </button>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-6 text-[11px] text-ink-subtle tracking-wide">
            <div className="flex-1 h-px bg-line" />
            <span>Or better yet…</span>
            <div className="flex-1 h-px bg-line" />
          </div>

          {/* Email + password form */}
          <form onSubmit={handleLogin} className="space-y-2.5">
            <div className="relative">
              <Mail size={13} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <select
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-bg-warm border border-line pl-10 pr-3 py-3 text-[13px] rounded-full focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-all appearance-none"
              >
                {users.map(u => <option key={u.email} value={u.email}>{u.email}</option>)}
              </select>
            </div>

            <div className="relative">
              <Lock size={13} strokeWidth={1.5} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className="w-full bg-bg-warm border border-line pl-10 pr-3 py-3 text-[13px] rounded-full focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber transition-all"
              />
            </div>

            <div className="text-right">
              <button type="button" className="text-[12px] text-ink-muted hover:text-ink underline underline-offset-2">Forgot password?</button>
            </div>

            <button
              type="submit"
              data-testid="login-submit"
              className="group w-full bg-ink text-white font-medium py-3.5 text-[13px] rounded-full mt-2 flex items-center justify-center gap-2.5 transition-all hover:bg-navy hover:shadow-[0_8px_20px_rgba(11,43,92,0.18)] active:scale-[0.99]"
            >
              <div className="w-4 h-4 rounded bg-amber flex items-center justify-center">
                <div className="grid grid-cols-3 gap-[1px] w-2 h-2">
                  {[1,1,1, 1,0,1, 1,1,1].map((v,i) => <span key={i} className={`w-[2px] h-[2px] ${v ? 'bg-ink' : 'bg-transparent'}`} />)}
                </div>
              </div>
              {tab === "login" ? "Log in to Atom AML" : "Create your Atom AML account"}
            </button>

            <input type="hidden" value={chosenInst?.id || ""} data-testid="login-institution" readOnly />
          </form>

          {/* Secondary tag at bottom */}
          <div className="mt-auto pt-8 text-center text-[11px] text-ink-subtle">
            Secured · CBN-aligned · NDPA 2023 · ISO 27001
          </div>
        </div>
      </div>

      {/* Welcome modal — Runway split-image style */}
      {welcomeOpen && (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-6 backdrop-blur-md animate-fade-in" onClick={() => { setWelcomeOpen(false); nav("/dashboard"); }}>
          <div className="bg-white max-w-[860px] w-full rounded-[20px] shadow-[0_4px_12px_rgba(11,43,92,0.08),0_32px_64px_rgba(11,43,92,0.25)] overflow-hidden grid grid-cols-2" onClick={(e) => e.stopPropagation()}>
            {/* Left — visual */}
            <div className="relative overflow-hidden flex items-center justify-center min-h-[480px]" style={{ background: "linear-gradient(135deg, #F4F1E8 0%, #EFEBDF 50%, #E8E1CC 100%)" }}>
              <div className="absolute inset-0 grain-bg opacity-40" />
              <div className="relative">
                <div className="w-[260px] h-[260px]">
                  <AtomArt />
                </div>
              </div>
              <div className="absolute top-5 left-5 inline-flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-risk-veryhigh/80" />
                <div className="w-2 h-2 rounded-full bg-risk-medium/80" />
                <div className="w-2 h-2 rounded-full bg-risk-low/80" />
              </div>
              <div className="absolute bottom-6 left-6 right-6 text-[10px] text-ink-muted tracking-wide">
                Atom Nucleus · Proton · Neutron · Electron
              </div>
            </div>

            {/* Right — dark content */}
            <div className="relative bg-ink text-white p-9 flex flex-col">
              <button onClick={() => { setWelcomeOpen(false); nav("/dashboard"); }} className="absolute top-5 right-5 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="welcome-close">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>

              <div className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/10 rounded-full text-[10px] tracking-wide font-medium w-fit mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber" />
                COMPLIANCE WORKSPACE · v1.0
              </div>

              <h2 className="display text-[28px] font-semibold tracking-[-0.03em] leading-[1.1] mb-3">
                Welcome to your<br />Atom AML workspace
              </h2>
              <p className="text-[12.5px] text-white/65 leading-relaxed mb-6">
                Your session preserves activity across customers, alerts, cases, screening, reporting, model governance and audit.
              </p>

              <div className="space-y-2.5 mb-6">
                {[
                  "1,500 customer profiles with 90-day baselines",
                  "12 seeded AMLTRIX typologies · live narratives",
                  "6 Atom AI models · explainability scored",
                  "5,000-entry tamper-evident audit log",
                  "Maker-checker enforced on consequential actions",
                ].map((t, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-[12px]">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber shrink-0 mt-0.5"><path d="M20 6L9 17l-5-5"/></svg>
                    <span className="text-white/80">{t}</span>
                  </div>
                ))}
              </div>

              <button data-testid="welcome-begin" onClick={() => { setWelcomeOpen(false); nav("/dashboard"); }} className="mt-auto w-full bg-white text-ink py-3 text-[13px] font-medium rounded-full hover:bg-bg-warm transition-colors flex items-center justify-center gap-2">
                Begin <ArrowRight size={13} strokeWidth={2} />
              </button>
              <div className="text-center text-[10px] text-white/40 mt-3">
                View all features in Settings
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
